import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useApp } from '../App';
import { formatCurrency } from '../utils/constants';

export default function DuesPanel() {
  const { user, currentTeam } = useApp();   // 2단계에서 운영자용 청구·승인 화면으로 교체한다
  const [settings, setSettings] = useState(null);
  const [monthlyDues, setMonthlyDues] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (currentTeam) loadDues(); }, [currentTeam]); // eslint-disable-line

  const loadDues = async () => {
    setLoading(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'teams', currentTeam.id, 'settings', 'dues'));
      if (settingsDoc.exists()) setSettings(settingsDoc.data());

      const q = query(collection(db, 'teams', currentTeam.id, 'monthlyDues'), orderBy('yearMonth', 'desc'));
      const snap = await getDocs(q);
      setMonthlyDues(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadPayments = async (monthId) => {
    try {
      const q = query(collection(db, 'teams', currentTeam.id, 'monthlyDues', monthId, 'payments'));
      const snap = await getDocs(q);
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const getMyPayment = () => {
    if (!user || !selectedMonth) return null;
    return payments.find(p => p.userId === user.uid);
  };

  const myPayment = getMyPayment();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 1200 }}>
      {/* 좌측: 회비 목록 */}
      <div style={s.panel}>
        <div style={s.panelHeader}><h2 style={s.panelTitle}>💰 회비 관리</h2></div>
        <div style={s.panelBody}>
          {settings && (
            <div style={{ padding: 16, backgroundColor: 'rgba(91,196,216,0.05)', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#5BC4D8', fontWeight: 600, marginBottom: 8 }}>회비 설정</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                <div><span style={s.label}>월회비</span> {formatCurrency(settings.monthlyFee)}</div>
                <div><span style={s.label}>게스트(회원)</span> {formatCurrency(settings.guestFeeForMember)}</div>
                <div><span style={s.label}>게스트(비회원)</span> {formatCurrency(settings.guestFeeForNonMember)}</div>
                <div><span style={s.label}>마감일</span> 매월 {settings.dueDayOfMonth || 25}일</div>
              </div>
              {settings.bankName && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#778899' }}>
                  입금: {settings.bankName} {settings.accountNumber} ({settings.accountHolder})
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, color: '#5BC4D8', marginBottom: 8 }}>월별 회비</div>
          {loading ? <div style={s.empty}>로딩 중...</div> :
            monthlyDues.length === 0 ? <div style={s.empty}>등록된 회비가 없습니다</div> :
            monthlyDues.map(md => (
              <div key={md.id} onClick={() => { setSelectedMonth(md); loadPayments(md.id); }}
                style={{ ...s.monthCard, backgroundColor: selectedMonth?.id === md.id ? 'rgba(91,196,216,0.08)' : 'rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>{md.yearMonth}</span>
                  <span style={{ color: '#5BC4D8', fontWeight: 600 }}>{formatCurrency(md.amount)}</span>
                </div>
                {md.description && <div style={{ fontSize: 12, color: '#778899', marginTop: 4 }}>{md.description}</div>}
              </div>
            ))}
        </div>
      </div>

      {/* 우측: 납부 현황 + 결제 */}
      <div style={s.panel}>
        <div style={s.panelHeader}><h2 style={s.panelTitle}>📝 납부 현황</h2></div>
        <div style={s.panelBody}>
          {selectedMonth ? (
            <>
              <div style={{ padding: '12px 4px', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16, color: '#5BC4D8' }}>{selectedMonth.yearMonth}</h3>
                <div style={{ fontSize: 13, color: '#778899' }}>금액: {formatCurrency(selectedMonth.amount)}</div>
              </div>

              {/* 회비 납부는 앱에서 — 웹은 운영 포털이다 (W1-04).
                  예전 웹 납부는 규칙 199-202 가 거부하는 경로에 쓰면서 포인트를 먼저 깎아
                  포인트만 사라졌다. 2단계에서 운영자용 청구·승인 화면으로 교체한다. */}
              {user && !myPayment && (
                <div style={{
                  padding: '14px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
                  color: '#8899AA', backgroundColor: 'rgba(91,196,216,0.05)',
                  border: '1px solid rgba(91,196,216,0.12)',
                }}>
                  회비 납부는 앱에서 해 주세요. 웹 포털은 운영 화면입니다.
                  {settings?.bankName && (
                    <div style={{ marginTop: 6, color: '#AAB4C0' }}>
                      입금 계좌: {settings.bankName} {settings.accountNumber} ({settings.accountHolder})
                    </div>
                  )}
                </div>
              )}

              {/* 이미 납부한 경우 */}
              {myPayment && (
                <div style={{ ...s.paySection, borderColor: 'rgba(74,222,128,0.2)', backgroundColor: 'rgba(74,222,128,0.05)' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#4ADE80', marginBottom: 8 }}>✅ 납부 완료</div>
                  <div style={{ fontSize: 13, color: '#AAB4C0' }}>
                    {myPayment.paidAt ? new Date(myPayment.paidAt.toDate()).toLocaleDateString('ko-KR') : ''} 납부
                  </div>
                  <div style={{ fontSize: 12, color: '#778899', marginTop: 4 }}>
                    결제수단: {myPayment.paymentMethod === 'point' ? '포인트' : myPayment.paymentMethod === 'kakaopay' ? '카카오페이' : '카드결제'}
                  </div>
                </div>
              )}

              {/* 전체 납부 목록 */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#5BC4D8', marginTop: 24, marginBottom: 8 }}>
                전체 납부 현황 ({payments.filter(p => p.status === 'paid').length}/{payments.length}명)
              </div>
              {payments.length === 0 ? (
                <div style={{ ...s.empty, padding: '30px 20px' }}>납부 기록이 없습니다</div>
              ) : (
                payments.map(p => (
                  <div key={p.id} style={s.payRow}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{p.userName || p.userId?.substring(0, 8)}</div>
                      <div style={{ fontSize: 11, color: '#778899' }}>
                        {p.paidAt ? new Date(p.paidAt.toDate()).toLocaleDateString('ko-KR') : ''}
                        {p.paymentMethod && ` · ${p.paymentMethod === 'point' ? '포인트' : p.paymentMethod === 'kakaopay' ? '카카오페이' : '카드'}`}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                      color: p.status === 'paid' ? '#4ADE80' : '#F87171',
                      backgroundColor: p.status === 'paid' ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                    }}>
                      {p.status === 'paid' ? '납부' : '미납'}
                    </span>
                  </div>
                ))
              )}
            </>
          ) : (
            <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>👈</div>월을 선택하세요</div>
          )}
        </div>
      </div>

    </div>
  );
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  panelHeader: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)' },
  panelTitle: { margin: 0, fontSize: 15, fontWeight: 600 },
  panelBody: { padding: 12, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  label: { color: '#667788', fontSize: 11 },
  monthCard: { padding: '14px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', borderLeft: '3px solid #5BC4D8' },
  payRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid rgba(91,196,216,0.04)' },
  paySection: {
    padding: 20, borderRadius: 10, marginBottom: 16, textAlign: 'center',
    border: '1px solid rgba(91,196,216,0.15)', backgroundColor: 'rgba(91,196,216,0.03)',
  },
  methodBtn: {
    flex: 1, padding: '10px 8px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    border: '1px solid rgba(91,196,216,0.15)', backgroundColor: 'rgba(255,255,255,0.02)', color: '#AAB4C0',
  },
  methodActive: {
    border: '1px solid #5BC4D8', backgroundColor: 'rgba(91,196,216,0.1)', color: '#5BC4D8', fontWeight: 600,
  },
  payBtn: {
    width: '100%', padding: '14px 0', borderRadius: 8, border: 'none', fontSize: 15, fontWeight: 700,
    background: 'linear-gradient(135deg, #5BC4D8, #4AABB8)', color: '#0A1220', cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(91,196,216,0.2)',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
  },
  modal: {
    backgroundColor: '#141E30', border: '1px solid rgba(91,196,216,0.15)', borderRadius: 16,
    padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  confirmRow: {
    display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 14,
    borderBottom: '1px solid rgba(91,196,216,0.04)',
  },
  cancelBtn: {
    flex: 1, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
    border: '1px solid rgba(91,196,216,0.15)', backgroundColor: 'transparent', color: '#778899',
  },
  confirmBtn: {
    flex: 2, padding: '12px 0', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    background: 'linear-gradient(135deg, #5BC4D8, #4AABB8)', color: '#0A1220',
  },
};
